import { useState, useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import {
  DateInputWithConfirm,
  InputWithConfirm,
  AddressInputWithConfirm,
  RadioOptionsWithConfirm,
  IQuestion,
  getStringFromDate,
  getDateFromString,
  isValidDate,
} from 'shared-components';

import { ApplicationState } from '../../../../../store';
import { saveVisaPrepForm } from '../../../../../store/student/actions';
import { createUpdateVPIFieldAction } from '../../../../../store/student/visaPrepForm/actions';
import QuestionPage from '../../../../templates/question';
import { VQ } from '../../../../../store/student/types';

import { Pages } from '../../../../../store/student/visaPrepForm/types';
import { getPageStatus } from './utils';
import { IDate } from '../../../../../types';
import { useNavigate } from 'react-router-dom';

const VisaPrepFormAddPIPage2 = () => {
  // dispatch actions
  const dispatch = useDispatch();
  const onNext = () => {
    dispatch(saveVisaPrepForm(Pages.PAGE1_2));
  };
  const onChange = (fieldName: string, value: any, refValue?: any) =>
    dispatch(createUpdateVPIFieldAction(fieldName, value, refValue));

  const navigate = useNavigate();
  const onBackToTaskList = () => navigate(-1);

  // state props from store
  const studentVisaPrepForm = useSelector(({ student }: ApplicationState) => student.visa, shallowEqual);

  const currentAddress = useSelector(({ student }: ApplicationState) => student.personalInfo.homeAddress, shallowEqual);

  const {
    residentialAddressStartDate = '',
    homeOwnershipStatus = '',
    homeOwnershipStatusOther = '',
    hasDifferentContactDetails = undefined,
    diffContactAddress = '',
    diffContactCity = '',
    diffContactPostcode = '',
    diffContactCountry = '',
  } = studentVisaPrepForm;

  const currentStep = 2;
  const steps = 2;

  // component state
  const [complete, setComplete] = useState(false);

  const [resAddressStartDate, setResAddressStartDate] = useState<IDate>(() =>
    getDateFromString(residentialAddressStartDate),
  );
  const [resAddressStartDateErrorMsg, setResAddressStartDateErrorMsg] = useState('');

  useEffect(() => {
    const { complete: newComplete } = getPageStatus({
      residentialAddressStartDate,
      homeOwnershipStatus,
      homeOwnershipStatusOther,
      hasDifferentContactDetails,
      diffContactAddress,
      diffContactCity,
      diffContactCountry,
      diffContactPostcode,
    });
    if (newComplete !== complete) {
      setComplete(newComplete);
    }
  }, [
    complete,
    residentialAddressStartDate,
    homeOwnershipStatus,
    homeOwnershipStatusOther,
    hasDifferentContactDetails,
    diffContactAddress,
    diffContactCity,
    diffContactCountry,
    diffContactPostcode,
  ]);

  const handleResAddressStartDateChange = (dateValue: IDate) => {
    const { YYYY, DD, MM } = dateValue;
    if (DD && MM && YYYY) {
      const isValid = isValidDate(dateValue);

      if (isValid) {
        onChange(VQ.RESIDENTIAL_ADDRESS_START_DATE, getStringFromDate(dateValue));
        setResAddressStartDateErrorMsg('');
      } else {
        onChange(VQ.RESIDENTIAL_ADDRESS_START_DATE, '');
        setResAddressStartDateErrorMsg(`Doesn't look like a valid date`);
      }
    }
  };

  return (
    <QuestionPage
      complete={complete}
      currentStep={currentStep}
      steps={steps}
      onNext={onNext}
      onBackToTaskList={onBackToTaskList}
      section={'Additional Personal Information'}
    >
      <DateInputWithConfirm
        key={VQ.RESIDENTIAL_ADDRESS_START_DATE}
        id={VQ.RESIDENTIAL_ADDRESS_START_DATE}
        label={'When did you start living at your current permanent residential address?'}
        helpText={`You entered the following as your current permanent residential address: ${currentAddress}`}
        value={resAddressStartDate}
        errorText={resAddressStartDateErrorMsg}
        onChangeDD={(evt: any) =>
          setResAddressStartDate({
            ...resAddressStartDate,
            DD: evt.currentTarget.value,
          })
        }
        onChangeMM={(evt: any) =>
          setResAddressStartDate({
            ...resAddressStartDate,
            MM: evt.currentTarget.value,
          })
        }
        onChangeYY={(evt: any) => {
          const { value } = evt.currentTarget;
          setResAddressStartDate({
            ...resAddressStartDate,
            YYYY: value,
          });
          if (value.length === 4) {
            handleResAddressStartDateChange({
              ...resAddressStartDate,
              YYYY: value,
            });
          }
        }}
        onBlur={(_evt: any) => handleResAddressStartDateChange({ ...resAddressStartDate })}
      />

      <RadioOptionsWithConfirm
        key={VQ.HOME_OWNERSHIP_STATUS}
        id={VQ.HOME_OWNERSHIP_STATUS}
        label={`What is the ownership status of your home?`}
        onClick={(question: IQuestion) => onChange(VQ.HOME_OWNERSHIP_STATUS, question.label)}
        questions={[
          {
            char: '1',
            label: 'I own it',
            selected: homeOwnershipStatus === 'I own it',
          },
          {
            char: '2',
            label: 'I rent it',
            selected: homeOwnershipStatus === 'I rent it',
          },
          {
            char: '3',
            label: 'I live with my family',
            selected: homeOwnershipStatus === 'I live with my family',
          },
          {
            char: '4',
            label: 'Other',
            selected: homeOwnershipStatus === 'Other',
          },
        ]}
      />

      {homeOwnershipStatus === 'Other' ? (
        <InputWithConfirm
          key={VQ.HOME_OWNERSHIP_STATUS_OTHER}
          id={VQ.HOME_OWNERSHIP_STATUS_OTHER}
          value={homeOwnershipStatusOther}
          label={'Give more details about your living situation, such as who you live with and who owns the property'}
          onChange={(evt: any) => {
            onChange(VQ.HOME_OWNERSHIP_STATUS_OTHER, evt.currentTarget.value);
          }}
        />
      ) : null}

      <RadioOptionsWithConfirm
        key={VQ.HAS_DIFFERENT_CONTACT_DETAILS}
        id={VQ.HAS_DIFFERENT_CONTACT_DETAILS}
        label={'Are your preferred contact details different from the contact details already provided above?'}
        onClick={(question: any) => onChange(VQ.HAS_DIFFERENT_CONTACT_DETAILS, question.label === 'Yes')}
        isInline={true}
        questions={[
          {
            char: 'Y',
            label: 'Yes',
            selected: hasDifferentContactDetails === true,
          },
          {
            char: 'N',
            label: 'No',
            selected: hasDifferentContactDetails === false,
          },
        ]}
      />

      {hasDifferentContactDetails ? (
        <AddressInputWithConfirm
          key={VQ.DIFF_CONTACT_ADDRESS}
          id={VQ.DIFF_CONTACT_ADDRESS}
          addressLabel="Contact Address"
          addressSubText="(e.g. unit 33, 17 Example Street)"
          cityLabel="City"
          postcodeLabel="Postcode (if you have one)"
          countryLabel="Country"
          addressValue={diffContactAddress}
          cityValue={diffContactCity}
          postcodeValue={diffContactPostcode}
          countryValue={diffContactCountry}
          onChangeAddress={(event: any) => {
            onChange(VQ.DIFF_CONTACT_ADDRESS, event.currentTarget.value);
          }}
          onChangeCity={(event: any) => {
            onChange(VQ.DIFF_CONTACT_CITY, event.currentTarget.value);
          }}
          onChangePostcode={(event: any) => {
            onChange(VQ.DIFF_CONTACT_POSTCODE, event.currentTarget.value);
          }}
          onChangeCountry={(value: string) => {
            onChange(VQ.DIFF_CONTACT_COUNTRY, value);
          }}
        />
      ) : null}
    </QuestionPage>
  );
};
export default VisaPrepFormAddPIPage2;
